import { TranslationCasingType, TranslationPluralType, translationsFromJson } from './common';
import { CustomTranslation } from './customTranslation';
import en from './languages/en.json';
import { Translation, TranslationLanguage } from './translation';
import { TranslationKey } from './TranslationKey';

export const englishTranslations = translationsFromJson(en);

class TranslationManager {
  public translations: Map<TranslationLanguage, Translation> = new Map([
    [TranslationLanguage.en, Translation.fromTranslations(TranslationLanguage.en, englishTranslations)],
  ]);
  private customTranslations: Map<string, CustomTranslation> = new Map();
  public language: TranslationLanguage = TranslationLanguage.en;

  get customTranslation() {
    if (!this.customTranslations.size) return undefined;
    return [...this.customTranslations.values()][0];
  }

  setTranslation(translation: Translation) {
    if (translation.language === TranslationLanguage.en) {
      for (const key of Object.keys(TranslationKey)) {
        const translationKey = key as TranslationKey;
        const value = translation.translations[translationKey];
        if (value) englishTranslations[translationKey] = value;
      }
    }

    this.language = translation.language;
    this.translations.set(translation.language, translation);
  }

  setCustomTranslation(customTranslation: CustomTranslation) {
    this.customTranslations.set(customTranslation.id, customTranslation);
  }
}

export const translationsManager = new TranslationManager();

export function t(key: TranslationKey, language?: TranslationLanguage | undefined) {
  return (
    translationsManager.customTranslation?.translations[key] ||
    translationsManager.translations.get(language || translationsManager.language)?.translations[key] ||
    englishTranslations[key] ||
    new TranslationPluralType(new TranslationCasingType(key))
  );
}

enum TranslationFormatKey {
  CHILD_PRODUCTS = 'CHILD_PRODUCTS',
  CHILD_CONTAINERS = 'CHILD_CONTAINERS',

  VALUE = 'VALUE',
}

export type TranslationFormatRecord = { [Key in TranslationFormatKey]?: string };

export function translate({
  key,
  language,
  formats,
  count = 'singular',
  casing = 'upper',
}: {
  key: TranslationKey;
  language?: TranslationLanguage | undefined;
  formats?: TranslationFormatRecord;
  count?: 'singular' | 'plural' | number;
  casing?: 'upper' | 'lower';
}): string {
  const pt = t(key, language);

  if (typeof count === 'number') count = count === 1 ? 'singular' : 'plural';

  let translation = pt[count][casing];

  if (!formats) return translation;
  for (const [key, value] of Object.entries(formats)) {
    translation = translation.replace(`{${key}}`, value);
  }
  return translation;
}

export function asTranslatedString(item: string): string {
  const key = item.charAt(0).toLowerCase().concat(item.substring(1).replaceAll(' ', ''));
  return (
    translationsManager.translations.get(translationsManager.language)?.translations[key as TranslationKey]?.singular
      .label || item
  );
}
